import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';

export interface TourStep {
  id: string;
  chapterNumber: number;
  chapterTitle: string;
  category: string;
  badge: string;
  title: string;
  content: string;
  proTip: string;
  iconName:
    | 'sparkles'
    | 'activity'
    | 'coins'
    | 'trending-up'
    | 'flame'
    | 'lightbulb'
    | 'zap'
    | 'layers'
    | 'radio'
    | 'bookmark'
    | 'search'
    | 'compass'
    | 'scale'
    | 'bar-chart'
    | 'grid'
    | 'arrow-left-right'
    | 'clock'
    | 'handshake'
    | 'book-open';
  route?: string;
  targetSelector: string;
}

export const TOUR_STEPS: TourStep[] = [
  // CHAPTER 1: COMMAND CENTER & MACRO REGIME
  {
    id: 'greeting',
    chapterNumber: 1,
    chapterTitle: 'Command Center',
    category: 'INTELLIGENCE DESK',
    badge: 'Step 1 of 12 · Cockpit',
    title: 'Personalized Market Intelligence Desk',
    content:
      'Welcome to your central Dalal Street command tower. Get real-time time-of-day greetings, live Indian Standard Time (IST) clocks, and instant market session status indicators (Live, Pre-market, Weekend, or Closed).',
    proTip:
      'Check the session indicator before 09:15 AM IST to review pre-market order structures, opening breadth bias, and overnight institutional flows.',
    iconName: 'sparkles',
    route: '/',
    targetSelector: '.ns-dashboard-greeting',
  },
  {
    id: 'executive-regime',
    chapterNumber: 1,
    chapterTitle: 'Command Center',
    category: 'MACRO REGIME',
    badge: 'Step 2 of 12 · Market Climate',
    title: 'Market Regime & 21 EMA Breadth',
    content:
      'Instead of relying on the headline Nifty 50 (which can be distorted by 2–3 index heavyweights), the regime bar calculates true Dalal Street participation across 2,000+ NSE equities using the >21 EMA ratio.',
    proTip:
      'Bullish Expansion (>50% > 21 EMA) is the green light for aggressive swing capital. When breadth drops below 40%, shift to defensive capital preservation.',
    iconName: 'activity',
    route: '/',
    targetSelector: '.ns-desk-executive-bar',
  },
  {
    id: 'market-pulse',
    chapterNumber: 1,
    chapterTitle: 'Command Center',
    category: 'LEVERAGE & HEALTH',
    badge: 'Step 3 of 12 · Smart Money Pulse',
    title: 'Market MTF Leverage & Pulse Tiers',
    content:
      'Monitor market-wide Margin Trading Facility (MTF) books across NSE and BSE, Advance/Decline close ratios, 52-week high/low expansions, and structural participation above key 200, 50, and 21 EMAs.',
    proTip:
      'Click the "Market MTF Leverage" card to view interactive historical MTF trends. Rapid margin spikes during a declining market warn of imminent forced liquidations.',
    iconName: 'coins',
    route: '/',
    targetSelector: '#section-pulse',
  },

  // CHAPTER 2: STRUCTURAL BREADTH & MOMENTUM
  {
    id: 'breadth-chart',
    chapterNumber: 2,
    chapterTitle: 'Breadth & Momentum',
    category: 'BREADTH CHARTS',
    badge: 'Step 4 of 12 · Structural Trend',
    title: 'Multi-Timeframe Breadth Structure',
    content:
      'Analyze moving average participation curves over 30D, 60D, and All sessions. Compare 21 EMA (momentum thrust), 50 EMA (intermediate trend), and 200 EMA (macro structural bull line) across the entire NSE universe.',
    proTip:
      'When the 21 EMA line crosses above the 50 EMA line while the market is consolidating, a powerful multi-week expansion rally is typically getting underway.',
    iconName: 'trending-up',
    route: '/',
    targetSelector: '#section-breadth .ns-chart-frame, #section-breadth > div:first-child',
  },
  {
    id: 'momentum-desk',
    chapterNumber: 2,
    chapterTitle: 'Breadth & Momentum',
    category: 'MARKET READ',
    badge: 'Step 5 of 12 · Tape Health',
    title: 'Market Stance & Tape Health Score',
    content:
      'The Momentum Desk provides a composite tape health score (/100) combining moving average alignment, volume thrust, and advance/decline ratios into an executive market verdict.',
    proTip:
      'A Tape Health Score above 60 confirms institutional sponsorship. A score below 40 signals that breakout setups have a higher probability of whipsaw.',
    iconName: 'activity',
    route: '/',
    targetSelector: '.ns-momentum-desk',
  },

  // CHAPTER 3: SETUP RADAR & SCANNERS
  {
    id: 'setup-volume-shockers',
    chapterNumber: 3,
    chapterTitle: 'Setup Radar',
    category: 'VOLUME SURGES',
    badge: 'Step 6 of 12 · Institutional Volume',
    title: 'Volume Shockers Panel',
    content:
      'Real-time radar surfacing NSE equities experiencing volume surges of 2x to 10x above their 30-day baseline, indicating aggressive institutional positioning before retail catches on.',
    proTip:
      'High Multiplier combined with Delivery % > 50% confirms genuine institutional absorption rather than speculative intraday churn.',
    iconName: 'zap',
    route: '/',
    targetSelector: '#section-setups .ns-setup-panel:nth-child(1)',
  },
  {
    id: 'setup-breakouts',
    chapterNumber: 3,
    chapterTitle: 'Setup Radar',
    category: 'BREAKOUT RADAR',
    badge: 'Step 7 of 12 · Stage-2 Breakouts',
    title: 'Breakout Setups Panel',
    content:
      'Screens for stocks breaking out of multi-week bases or clearing 52-week highs on expanding volume. Shows direct price changes and delivery accumulation markers.',
    proTip:
      'Click "View all breakouts →" at the bottom of the card to explore the full multi-timeframe Breakout Scanner engine.',
    iconName: 'lightbulb',
    route: '/',
    targetSelector: '#section-setups .ns-setup-panel:nth-child(2)',
  },
  {
    id: 'setup-overbought-oversold',
    chapterNumber: 3,
    chapterTitle: 'Setup Radar',
    category: 'MEAN REVERSION',
    badge: 'Step 8 of 12 · Extremes Radar',
    title: 'Overbought / Oversold Panel',
    content:
      'Calculates extreme price extensions away from moving averages alongside RSI stretch. Flags high-probability rubber-band oversold bounces and climactic overbought extensions.',
    proTip:
      'High-quality stocks hitting temporary oversold levels on low pullback volume offer optimal low-risk swing entries.',
    iconName: 'arrow-left-right',
    route: '/',
    targetSelector: '#section-setups .ns-setup-panel:nth-child(3)',
  },
  {
    id: 'stock-capsules',
    chapterNumber: 3,
    chapterTitle: 'Setup Radar',
    category: 'STOCK CAPSULES',
    badge: 'Step 9 of 12 · Interactive Badges',
    title: 'Interactive Stock Badges & Quick Chart Modals',
    content:
      'Notice the color-coded catalyst badges next to stock names: FII/DII buy tags, Bulk Deals (₹ Cr), and AI SEBI filing alerts. Click any stock symbol anywhere to open its interactive candlestick chart modal without losing your dashboard place.',
    proTip:
      'Hover over any stock capsule to preview its moving average posture, volume surge ratio, and technical diagnostic.',
    iconName: 'flame',
    route: '/',
    targetSelector: '#section-setups .ns-setup-panel:first-child table tbody tr:first-child td:first-child',
  },

  // CHAPTER 4: SECTOR FLOW & CONTROLS
  {
    id: 'industry-leadership',
    chapterNumber: 4,
    chapterTitle: 'Sector Flow & Tools',
    category: 'SECTOR ROTATION',
    badge: 'Step 10 of 12 · Capital Flow',
    title: 'Industry Sector Leadership Rankings',
    content:
      'Smart money rotates between sectors constantly. This section ranks Dalal Street sectors by >21 EMA breadth and institutional accumulation density so you always trade with the leading sector tailwind.',
    proTip:
      'Never swim against the tide: prioritize swing trades in stocks belonging to the top 10 leading sectors.',
    iconName: 'layers',
    route: '/',
    targetSelector: '#section-context .ns-setup-panel, #section-context',
  },
  {
    id: 'topbar-tickers',
    chapterNumber: 4,
    chapterTitle: 'Sector Flow & Tools',
    category: 'SMART MONEY PULSE',
    badge: 'Step 11 of 12 · FII / DII Tickers',
    title: 'Top Bar: Index Ticker & Institutional Cash Flow',
    content:
      'At the very top of your screen, track real-time Nifty 50 benchmarks alongside the live FII & DII Cash Market ticker showing net institutional buying or selling in crores (₹ Cr).',
    proTip:
      'When FII and DII are both net buyers (green cash flow), market rallies have strong institutional backing.',
    iconName: 'coins',
    route: '/',
    targetSelector: '.ns-topbar__right',
  },
  {
    id: 'global-search-controls',
    chapterNumber: 4,
    chapterTitle: 'Sector Flow & Tools',
    category: 'COMMAND PALETTE',
    badge: 'Step 12 of 12 · Complete',
    title: 'Universal Search & Theme Controls',
    content:
      'Press Ctrl+K (or Cmd+K) anywhere to search any of 2,000+ NSE stocks instantly. Use top bar controls to switch themes or re-launch this tour anytime with the Compass icon.',
    proTip:
      'You are all set to conquer Dalal Street with institutional clarity! Click Finish Tour to start exploring.',
    iconName: 'compass',
    route: '/',
    targetSelector: '[data-tour="global-search"]',
  },
];

export const TOUR_CHAPTERS = [
  { number: 1, title: 'Command Center', startStep: 0, count: 3 },
  { number: 2, title: 'Breadth & Momentum', startStep: 3, count: 2 },
  { number: 3, title: 'Setup Radar', startStep: 5, count: 4 },
  { number: 4, title: 'Sector Flow & Tools', startStep: 9, count: 3 },
];

interface AppTourContextType {
  isOpen: boolean;
  isPromptOpen: boolean;
  currentStepIndex: number;
  currentStep: TourStep;
  totalSteps: number;
  startTour: () => void;
  nextStep: () => void;
  prevStep: () => void;
  goToStep: (index: number) => void;
  skipTour: () => void;
  completeTour: () => void;
  openWelcomePrompt: () => void;
  closeWelcomePrompt: (dontShowAgain?: boolean) => void;
  hasCompletedTour: boolean;
}

const AppTourContext = createContext<AppTourContextType | undefined>(undefined);

const TOUR_STORAGE_PREFIX = 'ns_app_tour_completed_';
const TOUR_PROMPT_PREFIX = 'ns_app_tour_prompt_shown_';

export const AppTourProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [isOpen, setIsOpen] = useState(false);
  const [isPromptOpen, setIsPromptOpen] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  const storageKey = useMemo(() => {
    return `${TOUR_STORAGE_PREFIX}${user?.uid || 'guest'}`;
  }, [user?.uid]);

  const promptStorageKey = useMemo(() => {
    return `${TOUR_PROMPT_PREFIX}${user?.uid || 'guest'}`;
  }, [user?.uid]);

  const [hasCompletedTour, setHasCompletedTour] = useState<boolean>(() => {
    try {
      return localStorage.getItem(storageKey) === 'true';
    } catch {
      return false;
    }
  });

  // Keep state in sync if storage key changes (e.g. user logs in or out)
  useEffect(() => {
    try {
      const completed = localStorage.getItem(storageKey) === 'true';
      setHasCompletedTour(completed);
    } catch {
      setHasCompletedTour(false);
    }
  }, [storageKey]);

  // When user logs in or lands on dashboard, trigger the welcome tour prompt if never seen
  useEffect(() => {
    if (!isAuthenticated) return;

    try {
      const alreadyCompleted = localStorage.getItem(storageKey) === 'true';
      const promptAlreadyShown = localStorage.getItem(promptStorageKey) === 'true';

      if (!alreadyCompleted && !promptAlreadyShown) {
        // Small delay to let the dashboard render smoothly before greeting the user
        const timer = setTimeout(() => {
          setIsPromptOpen(true);
        }, 1200);

        return () => clearTimeout(timer);
      }
    } catch (e) {
      // Ignore storage errors in restricted contexts
    }
  }, [isAuthenticated, storageKey, promptStorageKey]);

  // Ensure routing matches the active step's target screen (all on dashboard /)
  const syncRouteForStep = useCallback(
    (stepIndex: number) => {
      const step = TOUR_STEPS[stepIndex];
      if (step?.route && location.pathname !== step.route) {
        navigate(step.route);
      }
    },
    [location.pathname, navigate]
  );

  const startTour = useCallback(() => {
    setIsPromptOpen(false);
    setCurrentStepIndex(0);
    setIsOpen(true);
    syncRouteForStep(0);
  }, [syncRouteForStep]);

  const completeTour = useCallback(() => {
    setIsOpen(false);
    setCurrentStepIndex(0);
    setHasCompletedTour(true);
    try {
      localStorage.setItem(storageKey, 'true');
      localStorage.setItem(promptStorageKey, 'true');
    } catch (e) {
      // Ignore
    }
    if (location.pathname !== '/' && location.pathname !== '/dashboard') {
      navigate('/');
    }
  }, [storageKey, promptStorageKey, location.pathname, navigate]);

  const skipTour = useCallback(() => {
    setIsOpen(false);
    try {
      localStorage.setItem(storageKey, 'true');
      localStorage.setItem(promptStorageKey, 'true');
    } catch (e) {
      // Ignore
    }
    if (location.pathname !== '/' && location.pathname !== '/dashboard') {
      navigate('/');
    }
  }, [storageKey, promptStorageKey, location.pathname, navigate]);

  const goToStep = useCallback(
    (index: number) => {
      if (index >= 0 && index < TOUR_STEPS.length) {
        setCurrentStepIndex(index);
        syncRouteForStep(index);
      }
    },
    [syncRouteForStep]
  );

  const nextStep = useCallback(() => {
    if (currentStepIndex < TOUR_STEPS.length - 1) {
      const nextIndex = currentStepIndex + 1;
      setCurrentStepIndex(nextIndex);
      syncRouteForStep(nextIndex);
    } else {
      completeTour();
    }
  }, [currentStepIndex, syncRouteForStep, completeTour]);

  const prevStep = useCallback(() => {
    if (currentStepIndex > 0) {
      const prevIndex = currentStepIndex - 1;
      setCurrentStepIndex(prevIndex);
      syncRouteForStep(prevIndex);
    }
  }, [currentStepIndex, syncRouteForStep]);

  const openWelcomePrompt = useCallback(() => {
    setIsPromptOpen(true);
  }, []);

  const closeWelcomePrompt = useCallback(
    (dontShowAgain: boolean = true) => {
      setIsPromptOpen(false);
      if (dontShowAgain) {
        try {
          localStorage.setItem(promptStorageKey, 'true');
        } catch (e) {
          // Ignore
        }
      }
    },
    [promptStorageKey]
  );

  const currentStep = TOUR_STEPS[currentStepIndex] || TOUR_STEPS[0];

  return (
    <AppTourContext.Provider
      value={{
        isOpen,
        isPromptOpen,
        currentStepIndex,
        currentStep,
        totalSteps: TOUR_STEPS.length,
        startTour,
        nextStep,
        prevStep,
        goToStep,
        skipTour,
        completeTour,
        openWelcomePrompt,
        closeWelcomePrompt,
        hasCompletedTour,
      }}
    >
      {children}
    </AppTourContext.Provider>
  );
};

export const useAppTour = (): AppTourContextType => {
  const context = useContext(AppTourContext);
  if (!context) {
    return {
      isOpen: false,
      isPromptOpen: false,
      currentStepIndex: 0,
      currentStep: TOUR_STEPS[0],
      totalSteps: TOUR_STEPS.length,
      startTour: () => {},
      nextStep: () => {},
      prevStep: () => {},
      goToStep: () => {},
      skipTour: () => {},
      completeTour: () => {},
      openWelcomePrompt: () => {},
      closeWelcomePrompt: () => {},
      hasCompletedTour: false,
    };
  }
  return context;
};

export default AppTourContext;
